import Divider from '@mui/material/Divider';
import { Calendar, Globe, Users } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { IoChevronDown, IoChevronUp, IoClose } from "react-icons/io5";
import activity from '../../../assets/activity.svg';
import DetailsCard from '../../../components/common/DetailsCard';
import IntentSignalCard from '../../../components/common/IntentSignalCard';
import Keywords from '../../../components/common/Keywords';
import { useSidebar } from '../../../context/SidebarContext';
import { IntentSignal } from './components/IntentSignalsTable';
import engagementIcon from '../../../assets/engage.svg';
import { useIntentSignal } from '../../../hooks/useIntentSignal';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import apiHandler from '../../../services/apiServices';



interface EditIntentProps {
  isOpen: boolean;
  onClose: () => void;
  signal: IntentSignal | null;
  currentIndex: number;
  totalCount: number;
  onNavigate: (direction: 'up' | 'down') => void;
  isNavigating: boolean;
  setIsNavigating: (value: boolean) => void;
  allSignals: IntentSignal[];
}

interface DetailItem {
  label: string;
  value: string | string[] | React.ReactNode;
  icon?: React.ElementType;
}

const EngagementIcon: React.FC = () => (
  <img src={engagementIcon} alt="Engagement" className="w-[14px] h-[14px]" />
);

const EditIntent: React.FC<EditIntentProps> = ({
  isOpen,
  onClose,
  signal,
  currentIndex,
  totalCount,
  onNavigate,
  isNavigating,
  setIsNavigating,
  allSignals
}) => {
  const navigate = useNavigate();
  const { isSidebarOpen } = useSidebar();
  const [selectedLeftPanel, setSelectedLeftPanel] = useState('activity');
  const [selectedRightPanel, setSelectedRightPanel] = useState('Details');
  const { updateIntentSignal, fetchNextPage, hasNextPage, isFetchingNextPage } = useIntentSignal();
  const [localSignal, setLocalSignal] = useState(signal);
  const [isToggling, setIsToggling] = useState(false);
  const [toggleState, setToggleState] = useState({
    isActive: signal?.status === 'active',
    isLoading: false,
    previousState: signal?.status === 'active'
  });
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalSignal(signal);
  }, [signal]);

  // Add new effect to handle pagination
  useEffect(() => {
    const checkAndFetchMore = async () => {
      // If we're close to the end of our current data (within 3 items)
      if (currentIndex >= totalCount - 3 && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
      }
    };

    checkAndFetchMore();
  }, [currentIndex, totalCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Update toggle state when signal changes
  useEffect(() => {
    setToggleState(prev => ({
      ...prev,
      isActive: signal?.status === 'active',
      previousState: signal?.status === 'active'
    }));
  }, [signal]);

  const handleEngagementClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!signal) return;
    navigate({
      to: '/accounts',
      search: { intent_signal_id: signal._id }
    });
  };

  const handleNavigate = async (direction: 'up' | 'down') => {
    if (!signal || !allSignals) return;
    
    const currentIndex = allSignals.findIndex(s => s._id === signal._id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < allSignals.length) {
      const nextSignal = allSignals[newIndex];
      window.history.pushState({}, '', `/intent-signals/${nextSignal._id}`);
      
      try {
        setIsNavigating(true);
        const response = await apiHandler.get(`/intent-signals/${nextSignal._id}`);
        if (response.data) {
          queryClient.setQueryData(['intentSignal', nextSignal._id], response.data);
          onNavigate(direction);
        }
      } catch (error) {
        console.error('Error fetching signal data:', error);
      } finally {
        setIsNavigating(false);
      }
    }
  };

  const handleClose = () => {
    onClose();
    window.history.pushState({}, '', '/intent-signals');
  };

  const handleSave = async () => {
    if (!signal) return;
    setIsSaving(true);
    
    try {
      await updateIntentSignal.mutateAsync({
        id: signal._id,
        keywords: localSignal?.keywords,
        rulesets: localSignal?.rulesets
      });
      
      toast.success('Changes saved successfully', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "bg-[#FFFFFF] shadow-lg border border-[#EEEEEE] p-4 rounded-xl max-w-[440px] w-full",
        progressClassName: "bg-[#29DC5B]"
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "bg-[#FFFFFF] shadow-lg border border-[#EEEEEE] p-4 rounded-xl max-w-[440px] w-full"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!signal) return null;

  // Generate details from actual signal data
  const details: DetailItem[] = [
    {
      label: "Created on",
      value: new Date(signal.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      icon: Calendar
    },
    {
      label: "Last run",
      value: new Date(signal.updated_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      icon: Calendar
    },
    {
      label: "Status",
      value: (
        <span className={`px-2 py-1 rounded-lg text-sm ${
          signal.status === 'active' ? 'bg-[#E8FFF0] text-[#388E3C]' :
          signal.status === 'inactive' ? 'bg-[#F8BBD0] text-[#C2185B]' :
          signal.status === 'created' ? 'bg-[#F0AD4E] text-[#6B4E15]' :
          signal.status === 'running' ? 'bg-[#A0C4FF] text-[#1B3B6F]' :
          'bg-[#F5F5F5] text-[#75777C]'
        }`}>
          {signal.status.charAt(0).toUpperCase() + signal.status.slice(1)}
        </span>
      ),
      icon: Globe
    },
    {
      label: "Lead count",
      value: (
        <a 
          href="#" 
          className="text-[#5D90D4] hover:underline"
          onClick={handleEngagementClick}
        >
          {signal.engagement_count}
        </a>
      ),
      icon: EngagementIcon
    }
  ];

  const handleCancel = () => {
    if (!signal) return;
    setLocalSignal(signal); // Reset to original signal
  };

  const showLoadingOverlay = isNavigating || isFetchingNextPage;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[100]"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className={`
        fixed 
        inset-y-[8px] 
        right-[8px] 
        ${isSidebarOpen ? 'left-[270px]' : 'left-[80px]'}
        bg-[#FFFFFF] 
        shadow-[0_0_15px_rgba(0,0,0,0.1),0_0_6px_rgba(0,0,0,0.05)] 
        z-[101] 
        flex 
        flex-col 
        rounded-[16px] 
        border 
        border-[#EEEFF1] 
        transition-all 
        duration-300
      `}>
        {/* Header with Navigation */}
        <div className="flex items-center justify-between px-[12px] py-[8px]">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleClose}
              className="hover:bg-gray-100 rounded-[4px] w-[28px] h-[28px] flex items-center justify-center"
            >
              <IoClose className="w-[16px] h-[16px] text-[#7A7A7A]" />
            </button>
            <div className="flex items-center gap-[6px]">
              <button 
                onClick={() => handleNavigate('up')}
                disabled={currentIndex === 0}
                className={`hover:bg-gray-100 rounded-[8px] w-[28px] h-[28px] flex items-center justify-center border border-[#EEEEEE] shadow-sm ${
                  currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <IoChevronUp className="w-[16px] h-[16px] text-[#7A7A7A]" />
              </button>
              <button 
                onClick={() => handleNavigate('down')}
                disabled={currentIndex === totalCount - 1}
                className={`hover:bg-gray-100 rounded-[8px] w-[28px] h-[28px] flex items-center justify-center border border-[#EEEEEE] shadow-sm ${
                  currentIndex === totalCount - 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <IoChevronDown className="w-[16px] h-[16px] text-[#7A7A7A]" />
              </button>
            </div>
            <span className="text-[#7A7A7A] text-sm font-[400]">
              {`${currentIndex + 1} of ${totalCount} in Intent signal`}
            </span>
          </div>
        </div>

        <Divider />

        <div className="flex items-center justify-between">
          {/* Intent Signal Name */}
          <div className="flex-none p-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-[16px] font-semibold text-[#232529] text-left">
                {signal.signal}
              </h2>
            </div>
          </div> 
          
          <div className="flex items-center gap-2">
            {/* Run intent signal button */}
            <div className="flex p-4">
              <button 
                onClick={async () => {
                  if (!signal || updateIntentSignal.isPending) return;
                  
                  try {
                    await updateIntentSignal.mutateAsync({
                      id: signal._id,
                      status: 'running'
                    });
                    
                    toast.success('Intent signal is now running');
                  } catch (error) {
                    console.error('Error running intent signal:', error);
                    toast.error('Failed to run intent signal');
                  }
                }}
                disabled={updateIntentSignal.isPending || signal?.status === 'running'}
                className={`
                  bg-[#29DC5B] text-[#000000] px-2 py-1 rounded-[8px] text-[14px] font-[400]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  hover:bg-[#22c55e] transition-colors
                `}
              >
                {updateIntentSignal.isPending ? 'Running...' : 'Run'}
              </button>
            </div>

            {/* toggle button */}
            <div className="flex p-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#7A7A7A]">Status:</span>
                <button
                  onClick={async () => {
                    if (!signal || isToggling || updateIntentSignal.isPending) return;
                    
                    const newStatus = !toggleState.isActive;
                    
                    // Immediately update UI state
                    setToggleState(prev => ({
                      ...prev,
                      isActive: newStatus,
                      isLoading: true,
                      previousState: prev.isActive
                    }));
                    
                    try {
                      await updateIntentSignal.mutateAsync({
                        id: signal._id,
                        status: newStatus ? 'active' : 'inactive'
                      });
                      
                      setToggleState(prev => ({
                        ...prev,
                        isLoading: false,
                        previousState: newStatus
                      }));
                      
                      toast.success(`Signal ${newStatus ? 'activated' : 'deactivated'} successfully`);
                    } catch (error) {
                      // Revert to previous state on error
                      setToggleState(prev => ({
                        ...prev,
                        isActive: prev.previousState,
                        isLoading: false
                      }));
                      
                      console.error('Error toggling signal status:', error);
                      toast.error('Failed to update signal status');
                    }
                  }}
                  disabled={isToggling || updateIntentSignal.isPending}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full 
                    ${toggleState.isActive ? 'bg-[#29DC5B]' : 'bg-[#D1D5DB]'}
                    transition-colors duration-200 ease-in-out
                    ${(toggleState.isLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`
                      ${toggleState.isActive ? 'translate-x-6' : 'translate-x-1'}
                      inline-block h-4 w-4 transform rounded-full 
                      ${toggleState.isLoading ? 'bg-[#F5F5F5] animate-pulse' : 'bg-[#FFFFFF]'}
                      transition duration-200 ease-in-out
                    `}
                  />
                </button>
                <span className="text-sm font-medium text-[#232529] capitalize">
                  {toggleState.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Divider />

        {/* Navigation and Content Area */}
        <div className="flex flex-col flex-1">
          {/* Table Header - Navigation Panels */}
          <div className="flex w-full border-b border-[#EEEEEE]">
            {/* Left Panel Header */}
            <div className="w-[58%] border-r border-[#EEEEEE] py-2 px-3">
              <button 
                onClick={() => setSelectedLeftPanel('activity')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedLeftPanel === 'activity' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <img src={activity} alt="Activity" className="w-[14px] h-[14px]" />
                <span className="text-[14px] font-[400]">Intent Signal</span>
              </button>
            </div>

            {/* Right Panel Header */}
            <div className="w-[42%] py-2 px-3">
              <button 
                onClick={() => setSelectedRightPanel('Details')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedRightPanel === 'Details' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <img src={activity} alt="Details" className="w-[14px] h-[14px]" />
                <span className="text-[14px] font-[400]">Details</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Panel Content */}
            <div className="w-[58%] border-r border-[#EEEEEE] overflow-y-auto scrollbar-hide" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <div className="p-[12px]">
                {selectedLeftPanel === 'activity' && (
                  <IntentSignalCard 
                    signal={{
                      signal: localSignal?.signal || '',
                      keywords: localSignal?.keywords,
                      rulesets: localSignal?.rulesets
                    }}
                    onAddKeyword={(keyword) => {
                      const updatedKeywords = [...(localSignal?.keywords || []), keyword];
                      setLocalSignal({
                        ...localSignal!,
                        keywords: updatedKeywords
                      });
                    }}
                    onRemoveKeyword={(keyword) => {
                      const updatedKeywords = (localSignal?.keywords || []).filter(k => k !== keyword);
                      setLocalSignal({
                        ...localSignal!,
                        keywords: updatedKeywords
                      });
                    }}
                    onAddRuleset={(ruleset) => {
                      const updatedRulesets = [...(localSignal?.rulesets || []), ruleset];
                      setLocalSignal({
                        ...localSignal!,
                        rulesets: updatedRulesets
                      });
                    }}
                    onRemoveRuleset={(ruleset) => {
                      const updatedRulesets = (localSignal?.rulesets || []).filter(r => r !== ruleset);
                      setLocalSignal({
                        ...localSignal!,
                        rulesets: updatedRulesets
                      });
                    }}
                    onSave={handleSave}
                    onCancel={() => handleCancel()}
                  />
                )}
              </div>
            </div>

            {/* Right Panel Content */}
            <div className="w-[42%] overflow-y-auto scrollbar-hide" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <div className="p-6">
                {selectedRightPanel === 'Details' && (
                  <DetailsCard
                    title="Details"
                    details={details}
                    className="w-full"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {showLoadingOverlay && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-50">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {isNavigating && (
          <div 
            className="absolute inset-0 bg-[#EEEEEE]/45 backdrop-blur-[2px] flex items-center justify-center z-50 transition-opacity duration-300"
            style={{
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#222222] border-t-transparent"></div>
              <span className="text-sm text-[#000000]">Loading...</span>
            </div>
          </div>
        )}

        {isSaving && (
          <div 
            className="absolute inset-0 bg-[#EEEEEE]/45 backdrop-blur-[2px] flex items-center justify-center z-50 transition-opacity duration-300"
            style={{
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#222222] border-t-transparent"></div>
              <span className="text-sm text-[#000000]">Saving changes...</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EditIntent;
